import { ChangeDetectionStrategy, Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IUser } from '@shared/auth/auth.models';
import { SharedService } from 'app/services/core/shared.service';
import { UserService } from 'app/services/user/user.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-useradministration',
  templateUrl: './user.administration.component.html',
  styleUrls: ['./user.administration.component.scss'],

  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserService],
})

export class UserAdministrationComponent implements OnInit {
  users: IUser[] = [];
  processing: boolean = false;

  constructor(public dialog: MatDialog,
    public userService: UserService,
    private toastr: ToastrService,
    public sharedService: SharedService) {
    this.loadUsers();
  }

  displayedColumns: string[] = this.getDisplayColumns();
  dataSource = new MatTableDataSource<IUser>(this.users);

  @ViewChild('TableUsersPaginator') paginator: MatPaginator;
  @ViewChild('TableUsersSort') sort: MatSort;
  @HostListener('window:resize', ['$event'])

  ngOnInit(): void {
  }

  getDisplayColumns(): string[] {
    return this.sharedService.mobile ?
      ['email', 'actions'] :
      ['userName', 'fullName', 'email', 'roles', 'actions']
  }

  onResize(event) {
    this.displayedColumns = this.getDisplayColumns();
  }

  filterTable(event: Event) {
    let value: string = (event.target as HTMLInputElement).value;
    this.dataSource.filter = value.trim().toLowerCase();
  }

  removeUser(username: string) {
    this.userService.removeUser(username).subscribe(u => {
      this.loadUsers();
      this.toastr.success("Removed user: " + username);
    },
      error => {
        console.error('remove error: ' + error.statusText);
      });
  }

  loadUsers() {
    this.processing = true;
    this.userService.getUsers().subscribe(u => {
      this.users = u;
      this.processing = false;
    },
      error => {
        console.error('error: ' + error.statusText);
        this.processing = false;
      },
      () => {
        this.dataSource.data = this.users;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      });
  }

  openRemoveUserDialog(user: IUser) {
    const dialogRef = this.dialog.open(DialogRemoveUserComponent, {
      data: {
        user: user
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == "Yes") {
        this.removeUser(user.userName);
      }
    });
  }

  openEditUserDialog(user: IUser) {
    const dialogRef = this.dialog.open(DialogEditUserComponent, {
      data: {
        user: user
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result != "Cancel") {
        this.loadUsers();
      }
    });
  }

  openAddUserDialog() {
    let newUser: IUser = {
      userName: "",
      fullName: "",
      email: "",
      roles: [],
      mobile: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      manager: "",
      jobTitle: "",
      active: true,
      isAuthenticated: true,
      timestamp: new Date()
    };
    const dialogRef = this.dialog.open(DialogAddUserComponent, {
      data: {
        user: newUser
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result != "Cancel") {
        this.loadUsers();
      }
    });
  }

}

//Remove User Dialog
@Component({
  selector: 'dialog-remove-user',
  templateUrl: 'remove-user.html',
})
export class DialogRemoveUserComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: any) { }
}

//Add User Dialog
@Component({
  selector: 'dialog-add-user',
  templateUrl: 'add-user.html',
  providers: [UserService],
})
export class DialogAddUserComponent implements OnInit {
  user: IUser = null;
  filter: string = "";
  processing: boolean = false;
  roles: string[] = [];
  userRoles: string[] = [];

  userInfo = new FormGroup({
    userName: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, Validators.email])
  });

  constructor(public userService: UserService,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<DialogAddUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      this.user = data.user;
  }
  ngOnInit(): void {
    // this.user = null;
    this.filter = "";

    //load roles
    this.userService.getRoles().subscribe(r => {
      this.roles = r;
    },
      error => {
        console.error('error: ' + error.statusText);
        this.toastr.error("Unable to load user roles");
      });

  }

  // findUser() {
  //   this.processing = true;
  //   this.userService.findUser(this.filter).subscribe(u => {
  //     this.user = u;
  //     if (this.user == null) {
  //       this.toastr.error("Unable to find user: " + this.filter);
  //     }
  //     this.processing = false;
  //   },
  //     error => {
  //       console.error('error: ' + error.statusText);
  //       this.processing = false;
  //     });
  // }

  updateUserRole(role: string, checkbox: MatCheckboxChange) {
    if (checkbox.checked) {
      this.userRoles.push(role);
    }
    else {
      let index = this.userRoles.indexOf(role);
      if (index >= 0) {
        this.userRoles.splice(index, 1)
      }
    }
  }

  addUser() {
    this.processing = true;
    this.userService.addUser(this.user).subscribe(u => {
      this.processing = false;
      this.toastr.success("Add user: " + this.user.fullName);
      this.user = null;
      this.filter = "";
      this.dialogRef.close();
    },
      error => {
        console.error('error: ' + error.statusText);
        this.processing = false;
        this.toastr.error("Unable to add user: " + this.user.fullName);
      });
  }

  saveUser() {
    this.processing = true;
    if (this.userInfo.invalid) {
      this.processing = false;
      return;
    } else {
      this.user.email = this.userInfo.get('email').value;
      this.user.userName = this.userInfo.get('userName').value;
      this.user.roles = this.userRoles;
      this.userService.addUser(this.user).subscribe(u => {
        this.processing = false;
        this.toastr.success("Saved user: " + this.user.userName);
        this.user = null;
        this.dialogRef.close();
      },
        error => {
          console.error('error: ' + error.statusText);
          this.processing = false;
          this.toastr.error("Unable to save user: " + this.user.userName);
        });
    }
  }
}


//Edit User Dialog
@Component({
  selector: 'dialog-edit-user',
  templateUrl: 'edit-user.html',
  providers: [UserService],
})
export class DialogEditUserComponent implements OnInit {
  user: IUser = null;
  processing: boolean = false;
  roles: string[] = [];

  userInfo = new FormGroup({
    userName: new UntypedFormControl(''),
    email: new UntypedFormControl('', [Validators.required, Validators.email])
  });

  constructor(public userService: UserService,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<DialogAddUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
  }

  ngOnInit(): void {
    this.user = null;
    this.userService.getRoles().subscribe(r => {
      this.roles = r;
    },
      error => {
        console.error('error: ' + error.statusText);
        this.toastr.error("Unable to load user roles");
      });

    //load user
    this.processing = true;
    if (this.data.user != null && this.data.user.email != '') {
      this.userService.getUser(this.data.user.userName).subscribe(u => {
        // console.log("user: ", u);
        this.user = u;
        this.userInfo.get('userName').setValue(this.user.userName);
        this.userInfo.get('email').setValue(this.user.email);
        this.processing = false;
      },
        error => {
          console.error('error: ' + error.statusText);
          this.toastr.error("Unable to user: " + this.data.user.userName);
          this.processing = false;
        });
    }

  }

  userHasRole(role) {
    if (this.user.roles.indexOf(role) >= 0) {
      return true;
    }
    return false;
  }

  saveUser() {
    this.processing = true;
    if (this.userInfo.invalid) {
      this.toastr.error("Please enter a valid email address");
      this.processing = false;
      return;
    } else {
      this.user.email = this.userInfo.get('email').value;
      this.userService.saveUser(this.user).subscribe(u => {
        this.processing = false;
        this.toastr.success("Saved user: " + this.user.userName);
        this.user = null;
        this.dialogRef.close();
      },
        error => {
          console.error('error: ' + error.statusText);
          this.processing = false;
          this.toastr.error("Unable to save user: " + this.user.userName);
        });
    }
  }

  updateUserRole(role: string, checkbox: MatCheckboxChange) {
    if (checkbox.checked) {
      this.user.roles.push(role);
    }
    else {
      let index = this.user.roles.indexOf(role);
      if (index >= 0) {
        this.user.roles.splice(index, 1)
      }
    }
  }

}
